import React, { useRef, useMemo } from 'react';
import { Bookmark, RefreshCw } from 'lucide-react';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import PostCard from '../components/PostCard';

export default function BookmarksPage() {
  const { user } = useAppContext();
  const { bookmarks, loading: bookmarksLoading, refetch } = useBookmarks();

  // Fetch all bookmarked events
  const { events, loading: eventsLoading } = useNostrQuery({
    filters: bookmarks.length > 0 ? [{ ids: bookmarks, limit: bookmarks.length }] : [],
    enabled: bookmarks.length > 0,
    allRelays: true,
  });

  // Keep the last non-empty events list so the UI doesn't flash empty
  // during background re-fetches when the filter temporarily changes.
  const prevEventsRef = useRef(events);
  if (events.length > 0) {
    prevEventsRef.current = events;
  }
  const stableEvents = events.length > 0 ? events : prevEventsRef.current;

  const loading = bookmarksLoading || eventsLoading;

  // Sort events to match bookmark order (newest bookmarks first)
  // Filter to only events that are still in the bookmark list
  const sortedEvents = useMemo(() => {
    const bookmarkSet = new Set(bookmarks);
    return [...stableEvents]
      .filter(e => bookmarkSet.has(e.id))
      .sort((a, b) => {
        const aIdx = bookmarks.indexOf(a.id);
        const bIdx = bookmarks.indexOf(b.id);
        // Later in the array = newer bookmark = show first
        return bIdx - aIdx;
      });
  }, [stableEvents, bookmarks]);

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-primary" /> Bookmarks
          </h1>
          <button
            onClick={refetch}
            disabled={bookmarksLoading}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            title="Refresh bookmarks"
          >
            <RefreshCw className={`w-4 h-4 ${bookmarksLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {bookmarks.length > 0 ? `${bookmarks.length} saved post${bookmarks.length !== 1 ? 's' : ''}` : 'Save posts to read later'}
        </p>
      </div>

      {!user ? (
        <div className="text-center py-16">
          <Bookmark className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Log in to see your bookmarks</p>
        </div>
      ) : loading && sortedEvents.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : sortedEvents.length === 0 ? (
        <div className="text-center py-16">
          <Bookmark className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No bookmarks yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Tap the bookmark icon on any post to save it here
          </p>
        </div>
      ) : (
        <div>
          {sortedEvents.map(event => (
            <PostCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

